import { useSyncExternalStore } from 'react';
import { ADMIN_OVERRIDE_PERMISSIONS } from '@inference.engine/permissions/web';

/**
 * Local-only escape hatch to drop the workspace-admin override so gating relies
 * purely on the granular scoped permissions — useful for exercising the
 * non-admin path without changing your role. It is persisted in localStorage
 * (survives reloads, syncs across tabs) and, in dev builds, exposed on
 * `window.__disableAdminOverride`.
 *
 * Toggle from the browser console (dev):
 *   __disableAdminOverride(true)   // ignore the org/workspace-admin fold → granular perms only
 *   __disableAdminOverride(false)  // restore the admin override
 *
 * Or anywhere in code via the exported `setAdminOverrideDisabled(boolean)`.
 * `useAdminOverrideDisabled` reads the flag through `useSyncExternalStore`, so
 * flipping it re-gates every mounted control live — no reload needed.
 */
const ADMIN_OVERRIDE_DISABLED_KEY = 'mf:disable-admin-override';
const adminOverrideListeners = new Set<() => void>();

function readStoredFlag(): boolean {
  try {
    return globalThis.localStorage.getItem(ADMIN_OVERRIDE_DISABLED_KEY) === '1';
  } catch {
    return false;
  }
}

// `useSyncExternalStore` calls getSnapshot on every render, so it reads this
// rather than hitting localStorage each time.
let adminOverrideDisabled = readStoredFlag();

function readAdminOverrideDisabled(): boolean {
  return adminOverrideDisabled;
}

export function setAdminOverrideDisabled(disabled: boolean): void {
  try {
    if (disabled) {
      globalThis.localStorage.setItem(ADMIN_OVERRIDE_DISABLED_KEY, '1');
    } else {
      globalThis.localStorage.removeItem(ADMIN_OVERRIDE_DISABLED_KEY);
    }
  } catch {
    return;
  }
  adminOverrideDisabled = disabled;
  adminOverrideListeners.forEach((listener) => listener());
}

function handleAdminOverrideStorage(event: StorageEvent) {
  if (event.key === ADMIN_OVERRIDE_DISABLED_KEY) {
    adminOverrideDisabled = readStoredFlag();
    adminOverrideListeners.forEach((listener) => listener());
  }
}

function subscribeAdminOverride(listener: () => void): () => void {
  if (adminOverrideListeners.size === 0) {
    // Nothing watched storage while unsubscribed, so another tab's change could
    // have landed unseen.
    adminOverrideDisabled = readStoredFlag();
    globalThis.addEventListener('storage', handleAdminOverrideStorage);
  }
  adminOverrideListeners.add(listener);
  return () => {
    adminOverrideListeners.delete(listener);
    if (adminOverrideListeners.size === 0) {
      globalThis.removeEventListener('storage', handleAdminOverrideStorage);
    }
  };
}

export function useAdminOverrideDisabled(): boolean {
  return useSyncExternalStore(subscribeAdminOverride, readAdminOverrideDisabled, () => false);
}

if (import.meta.env.DEV) {
  (
    globalThis as typeof globalThis & { __disableAdminOverride?: typeof setAdminOverrideDisabled }
  ).__disableAdminOverride = setAdminOverrideDisabled;
}

/**
 * Applies the org/workspace-admin override to a caller's required permissions:
 * folds `[OrganizationAdmin, WorkspaceAdmin]` into the set so admins satisfy any
 * gate (mirroring the backend's `withAdminPermissions`). When the dev escape
 * hatch is on, the admin roles are stripped instead, so gating reflects the raw
 * granular grants (and admin-only gates — which pass no granular perms — deny).
 */
export function applyAdminOverride(
  permissions: readonly string[],
  disabled: boolean,
): readonly string[] {
  return disabled
    ? permissions.filter((p) => !ADMIN_OVERRIDE_PERMISSIONS.some((admin) => admin === p))
    : [...ADMIN_OVERRIDE_PERMISSIONS, ...permissions];
}
